import React, { useEffect, useMemo, useRef, useState } from "react";
import LiquidGlass from "./LiquidGlass";
import { notify } from "./Notification";
import usePlayer, { player } from "../hooks/usePlayer";
import { resume } from "../data/resume";
import { RESUME_ITEMS as RESUME, best, contact, withTopHit } from "../data/search";
import notesIcon from "../assets/icons/dock/notes.png";
import finderIcon from "../assets/icons/dock/finder.png";
import zedIcon from "../assets/icons/dock/zed.png";
import chromeIcon from "../assets/icons/dock/chrome.png";
import ghosttyIcon from "../assets/icons/dock/ghostty.png";
import clockIcon from "../assets/icons/dock/clock.png";
import orbstackIcon from "../assets/icons/dock/orbstack.png";
import terminalIcon from "../assets/icons/dock/terminal.png";
import spotifyIcon from "../assets/icons/dock/spotify.png";
import mailIcon from "../assets/icons/macos/mail.png";
import safariIcon from "../assets/icons/macos/safari.png";
import controlCenterIcon from "../assets/icons/macos/control-center.png";
import batteriesIcon from "../assets/icons/macos/batteries.png";
import { ReactComponent as MagnifyingGlass } from "../assets/svgs/sf-symbols/magnifyingglass.svg";
import { ReactComponent as AppsGlyph } from "../assets/svgs/sf-symbols/spotlight/appstore.svg";
import { ReactComponent as FilesGlyph } from "../assets/svgs/sf-symbols/spotlight/folder.svg";
import { ReactComponent as ActionsGlyph } from "../assets/svgs/sf-symbols/spotlight/square.2.layers.3d.svg";
import { ReactComponent as ClipboardGlyph } from "../assets/svgs/sf-symbols/spotlight/document.on.document.svg";

// The four browse views, as in macOS Tahoe.
const VIEWS = [
  { id: "apps", label: "Applications", Glyph: AppsGlyph },
  { id: "files", label: "Files", Glyph: FilesGlyph },
  { id: "actions", label: "Actions", Glyph: ActionsGlyph },
  { id: "clipboard", label: "Clipboard", Glyph: ClipboardGlyph },
];

const openUrl = (href) => window.open(href, "_blank", "noopener");
const openResume = (anchor) => window.dispatchEvent(new CustomEvent("open-resume", { detail: { anchor } }));
const notOnThisMac = () =>
  notify(
    "That app doesn't open on this Mac. Search for a company or skill to jump into my resume."
  );

// Everything in the resume that's worth finding, each opening Notes at its spot.
const RESUME_ITEMS = RESUME.map((item) => ({ ...item, icon: notesIcon, kind: "In Resume", run: () => openResume(item.anchor) }));

const RESUME_FILE = {
  id: "file-resume",
  title: "Resume",
  subtitle: "Sylvester Asare Sarpong, in Notes",
  icon: notesIcon,
  kind: "Document",
  keywords: "cv sylvester",
  run: () => openResume(),
};

const RowIcon = ({ item, large }) =>
  item.Glyph ? (
    <span className={`sl-icon sl-icon--glyph ${large ? "sl-icon--large" : ""}`}>
      <item.Glyph />
    </span>
  ) : (
    <img src={item.icon} alt="" className={`sl-icon ${large ? "sl-icon--large" : ""}`} />
  );

const Spotlight = ({ open, onClose, onOpenControlCenter, lowPower, onToggleLowPower }) => {
  const [query, setQuery] = useState("");
  const [view, setView] = useState(null);
  const [selected, setSelected] = useState(0);
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const { playing, track } = usePlayer();

  // Fresh each time it opens, with the cursor in the field.
  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setView(null);
    setCopied(null);
    const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => setSelected(0), [query, view]);

  const email = contact("Email");
  const website = contact("Website");
  const linkedin = contact("LinkedIn");

  const apps = useMemo(
    () => [
      { id: "app-notes", title: "Notes", keywords: "resume cv", icon: notesIcon, run: () => openResume() },
      { id: "app-finder", title: "Finder", icon: finderIcon, run: notOnThisMac },
      {
        id: "app-zed",
        title: "Zed",
        keywords: "code editor source",
        icon: zedIcon,
        run: () => window.dispatchEvent(new Event("open-zed")),
      },
      {
        id: "app-chrome",
        title: "Google Chrome",
        keywords: "web browser use pouch",
        icon: chromeIcon,
        run: () => window.dispatchEvent(new Event("open-chrome")),
      },
      {
        id: "app-ghostty",
        title: "Ghostty",
        keywords: "terminal shell",
        icon: ghosttyIcon,
        run: () => window.dispatchEvent(new Event("open-ghostty")),
      },
      { id: "app-clock", title: "Clock", keywords: "time alarm timer", icon: clockIcon, run: notOnThisMac },
      {
        id: "app-orbstack",
        title: "OrbStack",
        keywords: "docker containers images",
        icon: orbstackIcon,
        run: () => window.dispatchEvent(new Event("open-orbstack")),
      },
      {
        id: "app-spotify",
        title: "Spotify",
        keywords: "music",
        icon: spotifyIcon,
        run: () => window.dispatchEvent(new Event("open-spotify")),
      },
      { id: "app-mail", title: "Mail", keywords: "email contact", icon: mailIcon, run: () => openUrl(email.href) },
      { id: "app-safari", title: "Safari", keywords: "web browser", icon: safariIcon, run: () => openUrl(website.href) },
      { id: "app-terminal", title: "Terminal", keywords: "shell", icon: terminalIcon, run: notOnThisMac },
    ].map((a) => ({ ...a, kind: "Application" })),
    [email, website]
  );

  const actions = useMemo(
    () => [
      {
        id: "act-music",
        title: playing ? "Pause Music" : "Play Music",
        subtitle: `${track.title}, ${track.artist}`,
        keywords: "play pause song spotify music",
        icon: spotifyIcon,
        run: player.toggle,
      },
      { id: "act-next", title: "Next Track", subtitle: "Spotify", keywords: "skip song music", icon: spotifyIcon, run: player.next },
      {
        id: "act-cc",
        title: "Open Control Center",
        keywords: "wifi bluetooth display sound airdrop focus",
        icon: controlCenterIcon,
        run: onOpenControlCenter,
      },
      {
        id: "act-lpm",
        title: lowPower ? "Turn Off Low Power Mode" : "Turn On Low Power Mode",
        keywords: "battery energy",
        icon: batteriesIcon,
        run: onToggleLowPower,
      },
      {
        id: "act-email",
        title: "Email Sylvester",
        subtitle: email.text,
        keywords: "contact mail hire message",
        icon: mailIcon,
        run: () => openUrl(email.href),
      },
      {
        id: "act-linkedin",
        title: "View LinkedIn Profile",
        subtitle: linkedin.text,
        keywords: "contact social",
        icon: safariIcon,
        run: () => openUrl(linkedin.href),
      },
      {
        id: "act-website",
        title: `Visit ${website.text.replace(/^www\./, "")}`,
        keywords: "website site portfolio",
        icon: safariIcon,
        run: () => openUrl(website.href),
      },
    ].map((a) => ({ ...a, kind: "Action" })),
    [playing, track, lowPower, onOpenControlCenter, onToggleLowPower, email, linkedin, website]
  );

  const clipboard = useMemo(
    () =>
      resume.contacts.map((c) => ({
        id: `clip-${c.label}`,
        title: c.text,
        subtitle: c.label,
        Glyph: ClipboardGlyph,
        copyText: c.label === "Email" ? c.text : c.href,
        kind: "Copy",
      })),
    []
  );

  // What the panel shows: a browse view (filtered by the query), or grouped search results.
  const q = query.trim().toLowerCase();
  const sections = useMemo(() => {
    if (view) {
      const items = { apps, files: [RESUME_FILE], actions, clipboard }[view];
      const shown = q ? best(items, q, 50) : items;
      return [{ title: null, items: shown, grid: view === "apps" }];
    }
    if (!q) return [];
    return withTopHit(
      [
        { title: "Applications", items: best(apps, q, 5) },
        { title: "Documents", items: best([RESUME_FILE, ...RESUME_ITEMS], q, 6) },
        { title: "Actions", items: best(actions, q, 5) },
      ],
      q
    );
  }, [view, q, apps, actions, clipboard]);

  const flat = sections.flatMap((s) => s.items);

  // Keep the highlighted result in view while arrowing through.
  useEffect(() => {
    const el = resultsRef.current && resultsRef.current.querySelector(".is-selected");
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const run = (item) => {
    if (!item) return;
    if (item.copyText) {
      if (navigator.clipboard) navigator.clipboard.writeText(item.copyText).catch(() => {});
      setCopied(item.id);
      setTimeout(onClose, 650);
      return;
    }
    onClose();
    item.run();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      run(flat[selected]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (query) setQuery("");
      else if (view) setView(null);
      else onClose();
    }
  };

  const viewLabel = view && VIEWS.find((v) => v.id === view).label;
  let index = -1;

  return (
    <div
      className={`spotlight-layer ${open ? "is-open" : ""}`}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      aria-hidden={!open}
    >
      <div className="spotlight" role="dialog" aria-label="Spotlight">
        <div className={`spotlight-bar ${q && !view ? "is-typing" : ""}`}>
          <LiquidGlass className="lg--light spotlight-field" bezel={16} depth={18}>
            <MagnifyingGlass className="spotlight-field-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={view ? `Search ${viewLabel}` : "Spotlight Search"}
              aria-label="Spotlight Search"
              aria-controls="spotlight-results"
              spellCheck={false}
              autoComplete="off"
            />
          </LiquidGlass>
          <div className="spotlight-views">
            {VIEWS.map(({ id, label, Glyph }) => (
              <LiquidGlass
                key={id}
                as="button"
                type="button"
                onClick={() => {
                  setView(view === id ? null : id);
                  if (inputRef.current) inputRef.current.focus();
                }}
                className={`lg--light spotlight-view ${view === id ? "is-active" : ""}`}
                aria-label={label}
                aria-pressed={view === id}
                title={label}
                tabIndex={q && !view ? -1 : 0}
              >
                <Glyph />
              </LiquidGlass>
            ))}
          </div>
        </div>

        {(q || view) && (
          <div className="spotlight-panel" id="spotlight-results" ref={resultsRef} role="listbox">
            {!flat.length && <p className="sl-empty">No Results</p>}
            {sections.map((section) => (
              <div key={section.title || "view"} className="sl-section">
                {section.title && <p className="sl-section-title">{section.title}</p>}
                <div className={section.grid ? "sl-grid" : ""}>
                  {section.items.map((item) => {
                    index += 1;
                    const i = index;
                    const isSelected = i === selected;
                    return section.grid ? (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onMouseMove={() => setSelected(i)}
                        onClick={() => run(item)}
                        className={`sl-tile ${isSelected ? "is-selected" : ""}`}
                      >
                        <img src={item.icon} alt="" />
                        <span>{item.title}</span>
                      </button>
                    ) : (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onMouseMove={() => setSelected(i)}
                        onClick={() => run(item)}
                        className={`sl-row ${section.top ? "sl-row--top" : ""} ${isSelected ? "is-selected" : ""}`}
                      >
                        <RowIcon item={item} large={section.top} />
                        <span className="sl-row-text">
                          <span className="sl-row-title">{item.title}</span>
                          {item.subtitle && <span className="sl-row-subtitle">{item.subtitle}</span>}
                        </span>
                        <span className="sl-row-kind">{copied === item.id ? "Copied" : item.kind}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Spotlight;
